import {useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {
    nextChatNotificationMuteRefreshDelay,
    replaceActiveChatNotificationMutes,
    useGetActiveChatNotificationMutesQuery,
} from '../services/chatNotificationMuteService.ts';
import {selectTenant, selectUserId} from '../store/features/Auth/authSlice.ts';
import {useStatusSubscription} from './useStatusSubscription.ts';

export const useChatNotificationMuteSync = (): void => {
    const tenant = useSelector(selectTenant);
    const userId = useSelector(selectUserId);
    const account = useMemo(
        () => ({
            tenant: tenant ?? '',
            userId: userId ?? '',
        }),
        [tenant, userId],
    );
    const shouldLoadMutes = account.tenant !== '' && account.userId !== '';
    const {currentData: activeMutes, refetch: refetchActiveMutes} =
        useGetActiveChatNotificationMutesQuery(account, {
            skip: !shouldLoadMutes,
            refetchOnMountOrArgChange: true,
        });
    const statusUserIds = useMemo(
        () => shouldLoadMutes ? [account.userId] : [],
        [account.userId, shouldLoadMutes],
    );
    const {getStatus} = useStatusSubscription({userIds: statusUserIds});
    const currentDndStatus = getStatus(account.userId)?.doNotDisturbUserStatus;
    const hasLoadedMutes = activeMutes !== undefined;
    const hasCurrentDndStatus = currentDndStatus !== undefined;

    useEffect(() => {
        replaceActiveChatNotificationMutes([]);
    }, [account.tenant, account.userId]);

    useEffect(() => {
        if (!shouldLoadMutes) {
            replaceActiveChatNotificationMutes([]);
            return;
        }
        if (!activeMutes) return;

        replaceActiveChatNotificationMutes(activeMutes);
        const refreshDelay = nextChatNotificationMuteRefreshDelay(activeMutes);
        if (refreshDelay === null) return;

        const expiryTimer = window.setTimeout(() => {
            void refetchActiveMutes();
        }, refreshDelay);

        return () => window.clearTimeout(expiryTimer);
    }, [activeMutes, refetchActiveMutes, shouldLoadMutes]);

    useEffect(() => {
        if (!shouldLoadMutes || !hasCurrentDndStatus || !hasLoadedMutes) return;
        void refetchActiveMutes();
    }, [
        currentDndStatus?.dueDate,
        currentDndStatus?.enabled,
        currentDndStatus?.lastUpdatedAt,
        hasCurrentDndStatus,
        hasLoadedMutes,
        refetchActiveMutes,
        shouldLoadMutes,
    ]);
};
